import React, { type ReactElement } from 'react';
import { capitalize, compact } from 'lodash';
import type { ProxyTypeEnum } from 'venn-api';
import {
  assert,
  assertNotNil,
  Dates,
  getFormattedProxyType,
  getProxyEndDate,
  getProxyStartDate,
  useHasFF,
} from 'venn-utils';
import { Separator } from '@/components/ui/separator';
import { formatDateByFrequency } from '../manage-data/utils';
import type { BulkManageRow, OldBulkManageRow, SubjectRangeAnalysisProxyInfo } from './types';
import {
  isBulkManageCashRow,
  isBulkManageFactorRow,
  isBulkManageFundRow,
  isBulkManageInvestmentRow,
  isBulkManageOptionRow,
  isBulkManageRow,
} from './utils';
import { OptionTooltipContent } from '../common/OptionTooltipContent';
import GenericRangeTooltipContent from '../common/GenericRangeTooltipContent';

interface ProxiedInvestmentTooltipContentProps {
  /** Data for a valid investment row
   *  It is expected to have returns and a frequency */
  data: BulkManageRow | OldBulkManageRow;
}

const isExtrapolating = (
  row: BulkManageRow | OldBulkManageRow,
): row is (BulkManageRow | OldBulkManageRow) & {
  extrapolateStartDate: number;
  extrapolateEndDate: number;
} =>
  isBulkManageRow(row)
    ? !!(isBulkManageFundRow(row) && row.extrapolateStartDate && row.extrapolateEndDate)
    : !!(row.extrapolateStartDate && row.extrapolateEndDate);

const hasProxyRange = (
  row: BulkManageRow | OldBulkManageRow,
): row is (BulkManageRow | OldBulkManageRow) & {
  startDate: number;
  proxyEndDate: number;
} =>
  isBulkManageRow(row)
    ? !!(isBulkManageFundRow(row) && row.startDate && row.proxyEndDate)
    : !!(row.startDate && row.proxyEndDate);

const getName = (data: BulkManageRow | OldBulkManageRow) => {
  if (isBulkManageRow(data) && isBulkManageCashRow(data) && data.allocationType === 'proforma') {
    return (data.allocation ?? 0) >= 0.0 ? 'Local Cash Rate (USD)' : 'SOFR + 30bps';
  }
  return data.name;
};

const getSymbol = (data: BulkManageRow | OldBulkManageRow): string | undefined => {
  if (isBulkManageRow(data)) {
    if (isBulkManageFundRow(data)) {
      return data.investment.symbol;
    }
    if (isBulkManageOptionRow(data)) {
      return data.symbol;
    }
  } else {
    return data.investment?.symbol;
  }
  return undefined;
};

const getProxyTooltipRows = ({
  idSuffix,
  proxyInfo,
  data,
}: {
  idSuffix: string | number;
  proxyInfo: SubjectRangeAnalysisProxyInfo;
  data: BulkManageRow | OldBulkManageRow;
}) => {
  const extrapolate = proxyInfo.proxyType !== 'EXTRAPOLATE' ? proxyInfo.extrapolate : false;
  const investment = 'investment' in data ? data.investment : undefined;
  const fundProxyInfo = 'fundProxyInfo' in data ? data.fundProxyInfo : undefined;

  const rows: {
    label: string;
    content: string | ReactElement;
    id: string;
  }[] = [];

  if (proxyInfo.proxyName) {
    rows.push({
      label: 'Returns Proxy',
      content: <strong>{proxyInfo.proxyName}</strong>,
      id: `returns-proxy-${idSuffix}`,
    });
  }

  if (proxyInfo.proxyType) {
    rows.push({
      label: 'Proxy Type',
      content: getFormattedProxyType(proxyInfo.proxyType),
      id: `returns-proxy-type-${idSuffix}`,
    });
  }

  const startRange = 'startDate' in data ? data.startDate : undefined;
  const endRange = 'endDate' in data ? data.endDate : undefined;
  const frequency = 'frequency' in data ? data.frequency : undefined;

  const unproxiedStartRange =
    investment && 'unproxiedStartRange' in investment
      ? investment.unproxiedStartRange
      : fundProxyInfo?.unproxiedStartDate;
  const unproxiedEndRange =
    investment && 'unproxiedEndRange' in investment ? investment.unproxiedEndRange : fundProxyInfo?.unproxiedEndDate;

  const extrapolateStartDate = 'extrapolateStartDate' in data ? data.extrapolateStartDate : undefined;
  const extrapolateEndDate = 'extrapolateEndDate' in data ? data.extrapolateEndDate : undefined;

  if (startRange && frequency && endRange) {
    let rangeStart = proxyInfo.proxyStartRange;
    if (proxyInfo.proxyType === 'EXTRAPOLATE') {
      rangeStart =
        extrapolateStartDate ??
        (unproxiedEndRange
          ? getProxyStartDate({
              proxyType: 'EXTRAPOLATE',
              proxiedStartDate: startRange,
              unproxiedEndDate: unproxiedEndRange,
              proxiedFrequency: frequency,
            })
          : proxyInfo.proxyStartRange);
    }

    let rangeEnd = proxyInfo.proxyEndRange;
    const isBackfillOrExtrapolate = ['BACKFILL', 'EXTRAPOLATE'].includes(proxyInfo.proxyType);
    if (proxyInfo.proxyType === 'EXTRAPOLATE' && extrapolateEndDate) {
      rangeEnd = extrapolateEndDate;
    } else if (isBackfillOrExtrapolate && unproxiedEndRange && unproxiedStartRange) {
      rangeEnd = getProxyEndDate({
        proxyType: proxyInfo.proxyType,
        extrapolate,
        unproxiedEndDate: unproxiedEndRange,
        proxiedStartDate: startRange,
        unproxiedStartDate: unproxiedStartRange,
        proxiedFrequency: frequency,
        proxiedEndDate: endRange,
      });
    }

    if (rangeStart && rangeEnd) {
      const startYear = Dates.toDDMMMYYYY(rangeStart, frequency);
      const endYear = Dates.toDDMMMYYYY(rangeEnd, frequency);

      rows.push({
        label: 'Proxy Range',
        content: `${startYear} - ${endYear}`,
        id: `proxy-range-${idSuffix}`,
      });
    }
  }

  if (proxyInfo.proxyNumLags) {
    rows.push({
      label: 'Lag',
      content: String(proxyInfo.proxyNumLags),
      id: `num-lags-${idSuffix}`,
    });
  }

  if (extrapolate) {
    rows.push({
      label: 'Proxy Type',
      content: 'Extrapolate',
      id: `extrapolate-proxy-type-${idSuffix}`,
    });

    if (extrapolateStartDate && extrapolateEndDate && frequency) {
      const startYear = Dates.toDDMMMYYYY(extrapolateStartDate, frequency);
      const endYear = Dates.toDDMMMYYYY(extrapolateEndDate, frequency);
      if (startYear && endYear) {
        rows.push({
          label: 'Proxy Range',
          content: `${startYear} - ${endYear}`,
          id: `extrapolate-proxy-range-${idSuffix}`,
        });
      }
    }
  }
  return rows;
};

const ProxiedInvestmentTooltipContent = ({ data }: ProxiedInvestmentTooltipContentProps) => {
  const hasLayeredProxyFF = useHasFF('layered_proxy_picker_ff');
  if (isBulkManageRow(data)) {
    assert(
      isBulkManageInvestmentRow(data) || isBulkManageFactorRow(data),
      `Expected a fund or factor row. Got: ${data.rowType}`,
    );
  }
  const frequency = assertNotNil(data.frequency);

  const isNewRow = 'fundProxyInfo' in data;
  const proxyType: ProxyTypeEnum | undefined = isNewRow
    ? data.fundProxyInfo?.proxyType
    : !isBulkManageRow(data)
      ? data.proxyType
      : undefined;
  const numLags = isNewRow ? data.fundProxyInfo?.numLags : undefined;
  const unproxiedFrequency = isNewRow ? data.fundProxyInfo?.unproxiedFrequency : undefined;
  const shouldShowZeroAllocationWarning = isBulkManageRow(data)
    ? (isBulkManageFundRow(data) || isBulkManageOptionRow(data)) && data.allocation === 0
    : !data.historicalStartDate && data.allocation === 0;
  const investmentTimeRange = `${Dates.toDDMMMYYYY(assertNotNil(data.startDate), frequency)} - ${Dates.toDDMMMYYYY(assertNotNil(data.endDate), frequency)}`;
  const name = getName(data);
  const symbol = getSymbol(data);

  const footer = shouldShowZeroAllocationWarning && (
    <div>
      <Separator className="bg-venn-grey-80" />
      <div className="mt-[4px] italic text-[12px] leading-[14px]">
        Investments with zero allocation do not constrain your analysis period.
      </div>
    </div>
  );

  if (isBulkManageRow(data) && isBulkManageOptionRow(data)) {
    return (
      <div className="p-[8px]">
        <OptionTooltipContent
          name={name}
          optionData={assertNotNil(data.optionData)}
          investmentTimeRange={investmentTimeRange}
          investmentSymbol={symbol}
          frequency={frequency}
          footer={footer}
        />
      </div>
    );
  }

  if (!hasLayeredProxyFF) {
    const legacyRows = compact([
      {
        label: 'Returns Availability Range',
        content: investmentTimeRange ?? '--',
        id: 'investment-range',
      },
      {
        label: 'Return Frequency',
        content: capitalize(frequency ?? '--'),
        id: 'return-frequency',
      },
      proxyType
        ? {
            label: 'Returns Proxy Type',
            content: getFormattedProxyType(proxyType),
            id: 'returns-proxy-type',
          }
        : null,
      numLags
        ? {
            label: `${unproxiedFrequency ? `${capitalize(unproxiedFrequency)} ` : ''}Lag:`,
            content: numLags,
            id: 'num-lags',
          }
        : null,
      isExtrapolating(data)
        ? {
            label: 'Extrapolation',
            content: 'On',
            id: 'extrapolation',
          }
        : null,
      hasProxyRange(data)
        ? {
            label: 'Proxy Range',
            content: (
              <>
                <div>
                  {formatDateByFrequency(data.startDate, frequency)} -{' '}
                  {formatDateByFrequency(data.proxyEndDate, frequency)}
                </div>
                {isExtrapolating(data) && (
                  <div>
                    {formatDateByFrequency(data.extrapolateStartDate, frequency)} -{' '}
                    {formatDateByFrequency(data.extrapolateEndDate, frequency)}
                  </div>
                )}
              </>
            ),
            id: 'proxy-range',
          }
        : null,
    ]);

    return (
      <div className="p-[8px]">
        <GenericRangeTooltipContent name={name} symbol={symbol} rows={legacyRows} footer={footer} />
      </div>
    );
  }

  const tooltipRowGroups: {
    label: string;
    content: string | ReactElement;
    id: string;
  }[][] = [
    [
      {
        label: 'Returns Availability Range',
        content: <strong>{investmentTimeRange ?? '--'}</strong>,
        id: 'investment-range',
      },
      {
        label: 'Returns Frequency',
        content: <strong>{capitalize(frequency ?? '--')}</strong>,
        id: 'return-frequency',
      },
    ],
  ];

  if ('proxyInfos' in data) {
    data.proxyInfos
      ?.filter((proxyInfo) => proxyInfo.proxyVariant === 'RETURN')
      ?.forEach((proxyInfo: SubjectRangeAnalysisProxyInfo, i) => {
        tooltipRowGroups.push(
          getProxyTooltipRows({
            data,
            proxyInfo,
            idSuffix: i + 1,
          }),
        );
      });
  }

  const filteredTooltipRowGroups = tooltipRowGroups.filter((group) => group.length > 0);

  return (
    <div className="p-[8px]">
      <GenericRangeTooltipContent name={name} symbol={symbol} rows={filteredTooltipRowGroups} footer={footer} />
    </div>
  );
};

export default ProxiedInvestmentTooltipContent;
